import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShareNetwork, Check, FacebookLogo, LinkSimple, DotsThreeCircle, UsersThree, CaretRight, Eye } from "@phosphor-icons/react";
import { shareOrigin } from "../lib/shareOrigin";
import { openSharePopup } from "../lib/sharePopup";
import { FB_GROUPS } from "../lib/fbGroups";
import GroupBlastModal from "./GroupBlastModal";
import { api } from "../lib/api";

/**
 * Reusable share button. Opens a small menu: Facebook (share dialog where
 * the user can pick Feed or a Group), Copy Link, and More… (native share
 * sheet). If the native share API silently fails (e.g. the Google app's
 * in-app browser), we fall back to copying the link with a "Copied ✓" flash.
 */
export default function InlineShareButton({
  url,
  title,
  text,
  caption,
  navigateTo,
  previewPath,
  onOpen,
  variant = "default",
  testid = "share-btn",
  className = "",
}) {
  const [copied, setCopied] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [blastOpen, setBlastOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const shareUrl =
    url ||
    (typeof window !== "undefined"
      ? `${shareOrigin()}${window.location.pathname}${window.location.search}`
      : "");
  const shareTitle = title || (typeof document !== "undefined" ? document.title : "Firewatch");

  // Unique URL per copy so Facebook always scrapes a fresh preview card
  const fresh = (u) => {
    if (!u || !u.includes("/api/share/")) return u;
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
    return `${u}${u.includes("?") ? "&" : "?"}v=${stamp}`;
  };

  useEffect(() => {
    if (!menuPos) {
      setGroupsOpen(false);
      return;
    }
    const close = (e) => {
      if (e && e.type === "scroll") {
        // Only close if the scrolled container actually moves the share button
        const t = e.target;
        const movesButton =
          t === document ||
          (t instanceof Node && btnRef.current && t.contains(btnRef.current));
        if (!movesButton) return;
      } else if (e && e.target instanceof Node && menuRef.current?.contains(e.target)) {
        return;
      }
      setMenuPos(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("click", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("click", close);
    };
  }, [menuPos]);

  const followUp = () => {
    if (navigateTo) navigate(navigateTo);
  };

  // Remember incident shares server-side (drives admin "re-share" nudges)
  const logShare = () => {
    const m = (shareUrl || "").match(/\/api\/share\/incident\/([\w-]{8,})/);
    if (m) {
      api.post("/metrics/share-log", { incident_id: m[1], name: shareTitle }).catch(() => {});
      window.dispatchEvent(
        new CustomEvent("fw-share-logged", {
          detail: { incident_id: m[1], at: new Date().toISOString() },
        }),
      );
    }
  };

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const copyLink = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(fresh(shareUrl)).catch(() => {});
    logShare();
    flashCopied();
    setMenuPos(null);
    setTimeout(followUp, 350);
  };

  const openFacebook = () => {
    if (caption && navigator.clipboard) {
      navigator.clipboard.writeText(caption).catch(() => {});
      toast.success("Caption copied — paste it into your Facebook post", { duration: 4000 });
    }
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fresh(shareUrl))}`,
      "_blank",
      "noopener,width=640,height=560",
    );
    logShare();
    setMenuPos(null);
    followUp();
  };

  const openGroup = (groupUrl) => {
    const body = caption ? fresh(caption) : fresh(shareUrl);
    if (navigator.clipboard) navigator.clipboard.writeText(body).catch(() => {});
    toast.success("Caption + link copied — paste it into a new group post", { duration: 5000 });
    openSharePopup(groupUrl);
    logShare();
    setMenuPos(null);
    followUp();
  };

  const nativeShare = () => {
    setMenuPos(null);
    navigator
      .share({ title: shareTitle, text: text || "", url: shareUrl })
      .then(() => { logShare(); followUp(); })
      .catch((err) => {
        // User dismissing the sheet is fine; anything else (blocked API,
        // Google app browser) falls back to copying the link.
        if (err && err.name !== "AbortError") copyLink();
        else followUp();
      });
  };

  const openMenu = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (menuPos) { setMenuPos(null); return; }
    onOpen?.();
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuW = 176;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - menuW - 8);
    const openUp = r.bottom + 260 > window.innerHeight;
    setMenuPos({ left, top: openUp ? null : r.bottom + 6, bottom: openUp ? window.innerHeight - r.top + 6 : null });
  };

  const canNative = typeof navigator !== "undefined" && Boolean(navigator.share);

  const menu = menuPos && (
    <div
      data-testid="share-menu"
      className="fixed z-[1300] w-44 bg-tac-bg border border-tac-border rounded-sm shadow-xl py-1"
      style={{ left: menuPos.left, top: menuPos.top ?? "auto", bottom: menuPos.bottom ?? "auto" }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      {previewPath && (
        <button
          type="button"
          data-testid="share-menu-preview"
          onClick={() => {
            setMenuPos(null);
            navigate(previewPath);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors border-b border-tac-border/60"
        >
          <Eye size={15} weight="bold" className="text-tac-primary" />
          Preview Post
        </button>
      )}
      <button
        type="button"
        data-testid="share-menu-facebook"
        onClick={openFacebook}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors"
      >
        <FacebookLogo size={15} weight="fill" style={{ color: "#1877F2" }} />
        Facebook
      </button>
      <button
        type="button"
        data-testid="share-menu-group"
        onClick={() => setGroupsOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors"
      >
        <UsersThree size={15} weight="fill" style={{ color: "#1877F2" }} />
        Post to Group
        <CaretRight
          size={11}
          weight="bold"
          className={`ml-auto text-tac-dim transition-transform ${groupsOpen ? "rotate-90" : ""}`}
        />
      </button>
      {groupsOpen && (
        <div
          data-testid="share-menu-group-list"
          className="overflow-y-auto tac-scroll overscroll-contain border-t border-b border-tac-border/60 bg-tac-surface/40"
          style={{ maxHeight: "min(340px, 42vh)" }}
        >
          {FB_GROUPS.map((g, idx) => (
            <button
              key={g.url}
              type="button"
              data-testid={`share-menu-group-${idx}`}
              onClick={() => openGroup(g.url)}
              className="w-full text-left px-3 py-1.5 text-[11px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors leading-snug"
            >
              {g.name}
            </button>
          ))}
          <div className="px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-tac-faint text-center border-t border-tac-border/40">
            {FB_GROUPS.length} groups · scroll for more
          </div>
        </div>
      )}
      <button
        type="button"
        data-testid="share-menu-blast"
        onClick={() => {
          setMenuPos(null);
          logShare();
          setBlastOpen(true);
        }}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors"
      >
        <UsersThree size={15} weight="bold" className="text-tac-primary" />
        Post to ALL Groups
      </button>
      <button
        type="button"
        data-testid="share-menu-copy"
        onClick={copyLink}
        className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors"
      >
        <LinkSimple size={15} weight="bold" className="text-tac-dim" />
        Copy Link
      </button>
      {canNative && (
        <button
          type="button"
          data-testid="share-menu-native"
          onClick={nativeShare}
          className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-tac-text hover:bg-tac-surface2 transition-colors"
        >
          <DotsThreeCircle size={15} weight="bold" className="text-tac-dim" />
          More Options…
        </button>
      )}
    </div>
  );

  if (variant === "compact") {
    return (
      <>
        <button
          ref={btnRef}
          type="button"
          onClick={openMenu}
          data-testid={testid}
          title="Share this incident"
          className={`inline-flex items-center justify-center w-7 h-7 border border-tac-border hover:border-tac-primary hover:text-tac-primary text-tac-dim rounded-sm transition-colors active:scale-95 ${className}`}
        >
          {copied ? (
            <Check size={12} weight="bold" className="text-tac-safe" />
          ) : (
            <ShareNetwork size={12} weight="bold" />
          )}
        </button>
        {menu}
        <GroupBlastModal open={blastOpen} onClose={() => setBlastOpen(false)} caption={caption ? fresh(caption) : fresh(shareUrl)} />
      </>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={openMenu}
        data-testid={testid}
        title="Share this incident"
        className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors active:scale-95 ${className}`}
      >
        {copied ? (
          <>
            <Check size={13} weight="bold" className="text-tac-safe" />
            <span className="text-tac-safe">Copied</span>
          </>
        ) : (
          <>
            <ShareNetwork size={13} weight="bold" />
            <span>Share</span>
          </>
        )}
      </button>
      {menu}
      <GroupBlastModal open={blastOpen} onClose={() => setBlastOpen(false)} caption={caption ? fresh(caption) : fresh(shareUrl)} />
    </>
  );
}
